import React, { useState, useEffect, useRef } from 'react';
import Modal from 'react-modal';

// Definer de prøver vi vil vise i dropdowns og auto-generate
const PRØVE_OPTIONS = ['PD1', 'PD2', 'PD3', 'SP'];

// Definér maks. deltagere pr. prøve
const MAX_DELTAGERE_PR_PRØVE = {
  'PD1': 25,
  'PD2': 40,
  'PD3': 40,
  'SP': 40,
};

function BulkCreateHoldModal({ isOpen, onRequestClose, terminer, aktivTerminId, sprogcenter, onBulkCreateSuccess }) {
  // Overordnede valg
  const [valgtTermin, setValgtTermin] = useState('');

  // State for den manuelle liste af hold
  const [holdRækker, setHoldRækker] = useState([{ prøve: '', deltagere: '' }]);

  // States for autogenerering
  const [mode, setMode] = useState('manuel');
  const [autoFormState, setAutoFormState] = useState({
    PD1: '',
    PD2: '',
    PD3: '',
    SP: ''
  });

  // Andre states
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const tableBodyRef = useRef(null);

  // Effekt til at nulstille og forudfylde, når modalen åbnes
  useEffect(() => {
    if (isOpen) {
      setValgtTermin(aktivTerminId?.toString() || (terminer.length > 0 ? terminer[0].id.toString() : ''));
      setHoldRækker([{ prøve: '', deltagere: '' }]);
      setAutoFormState({ PD1: '', PD2: '', PD3: '', SP: '' });
      setError(null);
      setSuccess(null);
      setMode('manuel');
    }
  }, [isOpen, aktivTerminId, terminer]);

  // Funktion til at håndtere ændringer i en specifik række (manuel mode)
  const handleRowChange = (index, field, value) => {
    const nyeRækker = [...holdRækker];
    nyeRækker[index][field] = value;
    setHoldRækker(nyeRækker);
  };

  const addRow = () => {
    const sidsteRække = holdRækker[holdRækker.length - 1];
    const nyRække = sidsteRække ? { ...sidsteRække } : { prøve: '', deltagere: '' };
    setHoldRækker([...holdRækker, nyRække]);
  };

  const removeRow = (index) => {
    if (holdRækker.length <= 1) return;
    const nyeRækker = holdRækker.filter((_, i) => i !== index);
    setHoldRækker(nyeRækker);
  };

  // Funktion til at håndtere auto-generering
  const handleAutoGenerate = () => {
    let nyeHold = [];
    let totalEleverIndtastet = 0;

    for (const prøve in autoFormState) {
      const samletAntalElever = parseInt(autoFormState[prøve], 10);
      
      if (isNaN(samletAntalElever) || samletAntalElever <= 0) {
          continue;
      }
      totalEleverIndtastet += samletAntalElever;

      const maxPrHold = MAX_DELTAGERE_PR_PRØVE[prøve];
      if (!maxPrHold) continue;

      const antalFuldeHold = Math.floor(samletAntalElever / maxPrHold);
      const resterendeElever = samletAntalElever % maxPrHold;

      for (let i = 0; i < antalFuldeHold; i++) {
          nyeHold.push({ prøve: prøve, deltagere: maxPrHold.toString() });
      }
      if (resterendeElever > 0) {
          nyeHold.push({ prøve: prøve, deltagere: resterendeElever.toString() });
      }
    }

    if (nyeHold.length === 0) {
      alert('Indtast venligst et antal elever for mindst én prøvetype.');
      return;
    }
    
    setHoldRækker(prevRækker => {
      const eksisterendeUdfyldteRækker = prevRækker.filter(r => r.prøve && r.deltagere);
      return [...eksisterendeUdfyldteRækker, ...nyeHold];
    });
    
    setMode('manuel');
  };

  const handleAutoFormChange = (e) => {
    const { name, value } = e.target;
    setAutoFormState(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError(null);
    setSuccess(null);
    setIsLoading(true);

    if (!sprogcenter || !sprogcenter.id || !valgtTermin) {
      setError('Fejl: Sprogcenter eller termin mangler.');
      setIsLoading(false);
      return;
    }

    const holdListe = holdRækker
      .map(række => ({ ...række, deltagere: parseInt(række.deltagere, 10) }))
      .filter(række => række.prøve && !isNaN(række.deltagere) && række.deltagere >= 0);

    if (holdListe.length === 0) {
      setError('Tilføj venligst mindst ét gyldigt hold med prøve og deltagere.');
      setIsLoading(false);
      return;
    }

    const payload = {
      sprogcenter_id: sprogcenter.id,
      termin_id: parseInt(valgtTermin, 10),
      holdListe
    };
    
    try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/hold/bulk`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
            credentials: 'include'
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.message || 'Serverfejl');
        
        setSuccess(result.message);
        onBulkCreateSuccess(parseInt(valgtTermin, 10));
        setTimeout(() => {
            onRequestClose();
        }, 2500);

    } catch (err) {
        setError(`Fejl: ${err.message}`);
    } finally {
        setIsLoading(false);
    }
  };

  const handleClose = () => {
      setHoldRækker([{ prøve: '', deltagere: '' }]);
      setError(null);
      setSuccess(null);
      onRequestClose();
  };

  return (
    <Modal isOpen={isOpen} onRequestClose={handleClose} contentLabel="Opret Hold" className="ReactModal__Content" overlayClassName="ReactModal__Overlay">
      <form onSubmit={handleSubmit} className="modal-form">
        <h3>Opret Hold for {sprogcenter?.navn || '...'}</h3>
        {error && <p className="error-message">{error}</p>}
        {success && <p className="success-message-inline">{success}</p>}

        <div className="form-row">
          <div className="form-field">
            <label>Sprogcenter</label>
            <p className="form-static-text">{sprogcenter?.navn || 'Intet sprogcenter valgt'}</p>
          </div>
          <div className="form-field">
            <label htmlFor="bulkTermin">Termin*</label>
            <select id="bulkTermin" value={valgtTermin} onChange={e => setValgtTermin(e.target.value)} required>
                <option value="">-- Vælg Termin --</option>
                {terminer.map(t => <option key={t.id} value={t.id}>{t.navn}</option>)}
            </select>
          </div>
        </div>

        <div className="view-toggle-container" style={{maxWidth: '100%', marginBottom: '20px'}}>
            <button type="button" className={`toggle-button ${mode === 'manuel' ? 'active' : ''}`} onClick={() => setMode('manuel')}>Manuel Indtastning</button>
            <button type="button" className={`toggle-button ${mode === 'auto' ? 'active' : ''}`} onClick={() => setMode('auto')}>Autogenerer Hold</button>
        </div>

        {mode === 'manuel' ? (
            <>
                <div className="table-container" style={{maxHeight: '300px', overflowY: 'auto' }} ref={tableBodyRef}>
                    <table className="censor-table bulk-create-table">
                        <thead>
                            <tr>
                                <th>Prøve</th>
                                <th>Antal Deltagere</th>
                                <th style={{width: '60px'}}></th>
                            </tr>
                        </thead>
                        <tbody>
                            {holdRækker.map((række, index) => (
                                <tr key={index}>
                                    <td>
                                        <select value={række.prøve} onChange={e => handleRowChange(index, 'prøve', e.target.value)}>
                                            <option value="">-- Vælg --</option>
                                            {PRØVE_OPTIONS.map(p => <option key={p} value={p}>{p}</option>)}
                                        </select>
                                    </td>
                                    <td>
                                        <input 
                                            type="number" placeholder="f.eks. 15" min="0"
                                            value={række.deltagere}
                                            onChange={e => handleRowChange(index, 'deltagere', e.target.value)}
                                        />
                                    </td>
                                    <td className="action-cell">
                                        <button type="button" onClick={() => removeRow(index)} className="delete-button icon-button" title="Fjern Række" disabled={holdRækker.length <= 1}>
                                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="18px" height="18px"><path fillRule="evenodd" d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25zm3 10.5a.75.75 0 01-1.5 0V9.75a.75.75 0 011.5 0v3zm-4.5 0a.75.75 0 01-1.5 0V9.75a.75.75 0 011.5 0v3z" clipRule="evenodd" /></svg>
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <button type="button" onClick={addRow} className="add-row-button">
                  + Tilføj Række (Kopierer sidste)
                </button>
            </>
        ) : (
            <div className="auto-generate-section">
                 <div className="form-row">
                    <div className="form-field">
                        <label htmlFor="autoPD1">Antal Elever til PD1 (Maks {MAX_DELTAGERE_PR_PRØVE.PD1})</label>
                        <input type="number" id="autoPD1" name="PD1" value={autoFormState.PD1} onChange={handleAutoFormChange} placeholder="Indtast samlet antal" />
                    </div>
                    <div className="form-field">
                        <label htmlFor="autoPD2">Antal Elever til PD2 (Maks {MAX_DELTAGERE_PR_PRØVE.PD2})</label>
                        <input type="number" id="autoPD2" name="PD2" value={autoFormState.PD2} onChange={handleAutoFormChange} placeholder="Indtast samlet antal" />
                    </div>
                 </div>
                 <div className="form-row">
                    <div className="form-field">
                        <label htmlFor="autoPD3">Antal Elever til PD3 (Maks {MAX_DELTAGERE_PR_PRØVE.PD3})</label>
                        <input type="number" id="autoPD3" name="PD3" value={autoFormState.PD3} onChange={handleAutoFormChange} placeholder="Indtast samlet antal" />
                    </div>
                    <div className="form-field">
                        <label htmlFor="autoSP">Antal Elever til SP (Maks {MAX_DELTAGERE_PR_PRØVE.SP})</label>
                        <input type="number" id="autoSP" name="SP" value={autoFormState.SP} onChange={handleAutoFormChange} placeholder="Indtast samlet antal" />
                    </div>
                 </div>
                <button type="button" onClick={handleAutoGenerate} className="toggle-form-button primary-action" style={{width: '100%', marginTop: '10px'}}>
                    Generer Hold til Listen
                </button>
                <small style={{display:'block', textAlign:'center', marginTop:'5px'}}>Dette tilføjer de beregnede hold til den manuelle liste, hvor du kan finjustere dem før oprettelse.</small>
            </div>
        )}

        <div className="modal-actions">
          <button type="submit" disabled={isLoading}>{isLoading ? 'Opretter...' : 'Opret Alle Hold Fra Listen'}</button>
          <button type="button" onClick={handleClose} className="cancel-button">Annuller</button>
        </div>
      </form>
    </Modal>
  );
}

export default BulkCreateHoldModal;